import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faWarehouse,
  faMapMarkerAlt,
  faMapMarkedAlt,
  faToggleOn,
  faToggleOff,
  faBoxes,
  faFlag,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import warehouseService from '../../services/warehouseService';

// In-flight request dedupe map (module-level) — ensures only one network call per id at a time
const inFlightRequests = new Map();

const InfoRow = ({ icon, label, value, highlight = false }) => {
  const { t } = useTranslation();
  const emDash = t('common.symbols.emDash');
  const displayValue = value || emDash;
  return (
    <div
      className="flex items-start justify-between rounded-xl border px-4 py-3"
      style={{
        borderColor: UI_COLORS.BORDERS.LIGHT,
        backgroundColor: highlight
          ? colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.06)
          : UI_COLORS.BACKGROUNDS.CARD,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.25),
            color: UI_COLORS.TEXT.PRIMARY,
          }}
        >
          <FontAwesomeIcon icon={icon} className="text-base" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
            {label}
          </p>
          <p className="text-base font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {displayValue}
          </p>
        </div>
      </div>
    </div>
  );
};

const WarehouseDetails = ({ isOpen, onClose, warehouse: initialWarehouse = null, warehouseId = null }) => {
  const { t } = useTranslation();

  const STATE_OPTIONS = t('locations.states', { returnObjects: true });
  const resolveStateLabel = (raw) => {
    if (!raw) return null;
    const match = STATE_OPTIONS.find(
      (o) => o.value === raw || o.label.toLowerCase() === raw.toLowerCase()
    );
    return match ? match.label : raw;
  };

  const formatCapacity = (capacity) => {
    const emDash = t('common.symbols.emDash');
    if (capacity === null || capacity === undefined || capacity === '') return emDash;
    const n = Number(capacity);
    if (Number.isNaN(n)) return emDash;
    const rounded = Math.round(n);
    return `${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} m²`;
  };

  // Use a ref to hold cache and avoid changing across renders (prevents eslint dependency warnings)
  const cacheRef = React.useRef(null);
  if (cacheRef.current === null) {
    const winCache = (typeof window !== 'undefined' && window.warehouseDetailsCache) || {};
    if (typeof window !== 'undefined' && !window.warehouseDetailsCache) {
      window.warehouseDetailsCache = winCache;
    }
    cacheRef.current = winCache;
  }

  const [loading, setLoading] = React.useState(false);
  const [warehouse, setWarehouse] = React.useState(initialWarehouse);

  React.useEffect(() => {
    // If parent passed a full warehouse object, use it and cache it (but still fetch fresh data)
    if (initialWarehouse && initialWarehouse.id) {
      setWarehouse(initialWarehouse);
      cacheRef.current[initialWarehouse.id] = initialWarehouse;
    }

    // If no id or not open, clear local state
    if (!isOpen || !warehouseId) {
      setWarehouse(null);
      return;
    }

    // If cached, show cached value immediately while we refresh in background
    if (cacheRef.current[warehouseId]) {
      setWarehouse(cacheRef.current[warehouseId]);
    }

    // Always fetch fresh data when modal opens
    let mounted = true;
    const fetchById = async (force = true) => {
      setLoading(true);
      try {
        const useForce = Boolean(force) || !cacheRef.current[warehouseId];

        // Reuse an in-flight promise if present to avoid duplicate network calls
        let promise = inFlightRequests.get(warehouseId);
        if (!promise) {
          promise = warehouseService.getWarehouseById(warehouseId, { force: useForce });
          inFlightRequests.set(warehouseId, promise);
        }

        const res = await promise;

        // cleanup stored promise
        inFlightRequests.delete(warehouseId);

        if (res && res.success) {
          if (!mounted) return;
          cacheRef.current[warehouseId] = res.data;
          setWarehouse(res.data);
        } else {
          // ensure we clear on failure
          if (!mounted) return;
          setWarehouse(null);
        }
      } catch (err) {
        // ensure in-flight entry removed on error
        inFlightRequests.delete(warehouseId);
        if (!mounted) return;
        setWarehouse(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void fetchById(true);
    return () => {
      mounted = false;
    };
  }, [isOpen, warehouseId, initialWarehouse]);

  if (!isOpen) return null;

  const emDash = t('common.symbols.emDash');

  // Format display values
  const warehouseName = warehouse?.name || emDash;
  const address = warehouse?.address || emDash;
  const totalCapacity = formatCapacity(warehouse?.total_capacity);
  const isActiveLabel = warehouse?.is_active ? 'Active' : 'Inactive';
  const googleMapsUrl = warehouse?.google_maps_url;

  // Format full address for display
  const fullAddress = [address].filter(Boolean).join(', ') || emDash;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.warehouse-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .warehouse-view-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="warehouse-view-modal w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {t('warehouses.view.header.sectionTitle')}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {warehouse?.name ? `${warehouse.name}` : t('warehouses.view.header.fallbackTitle')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
              ></div>
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {t('warehouses.view.loading')}
              </p>
            </div>
          ) : !warehouse ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon
                icon={faWarehouse}
                className="text-4xl opacity-60"
                style={{ color: UI_COLORS.TEXT.MUTED }}
              />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('warehouses.view.empty.title')}
              </h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {t('warehouses.view.empty.message')}
              </p>
            </div>
          ) : (
            <>
              {/* Basic Information */}
              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faWarehouse} label={t('warehouses.view.fields.warehouseName')} value={warehouseName} highlight />
                <InfoRow icon={faBoxes} label={t('warehouses.view.fields.totalCapacity')} value={totalCapacity} highlight />
                <InfoRow
                  icon={warehouse?.is_active ? faToggleOn : faToggleOff}
                  label={t('warehouses.view.fields.status')}
                  value={isActiveLabel}
                />
              </div>

              {/* Address Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  {t('warehouses.view.sections.addressInformation')}
                </h3>
                <div className="grid gap-4 md:grid-cols-1">
                  <InfoRow icon={faMapMarkerAlt} label={t('warehouses.view.fields.fullAddress')} value={fullAddress} />
                  {warehouse?.state && (
                    <InfoRow icon={faFlag} label="State" value={resolveStateLabel(warehouse.state)} />
                  )}
                </div>
              </div>

              {/* Contact Information */}
              {/* Contact information removed per spec (only shown in add form) */}

              {/* Google Maps */}
              {googleMapsUrl && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {t('warehouses.view.sections.location')}
                  </h3>
                  <div className="grid gap-4 md:grid-cols-1">
                    <div
                      className="flex items-start justify-between rounded-xl border px-4 py-3"
                      style={{
                        borderColor: UI_COLORS.BORDERS.LIGHT,
                        backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-10 w-10 items-center justify-center rounded-lg"
                          style={{
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.25),
                            color: UI_COLORS.TEXT.PRIMARY,
                          }}
                        >
                          <FontAwesomeIcon icon={faMapMarkedAlt} className="text-base" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {t('warehouses.view.fields.googleMaps')}
                          </p>
                          <a
                            href={googleMapsUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-base font-medium hover:underline"
                            style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }}
                          >
                            {t('warehouses.view.fields.viewOnGoogleMaps')}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarehouseDetails;